import axios from "axios";
import CryptoJS from "crypto-js";
import { logoutRequest } from "../auth/actions/authActions";
import store from "../store";
import { NEW_BOOKING_PAGES } from "../../component/common/Routes/NewBookingPages";

const SECRET_KEY = "843567893696976453275974432697R634976";

const axiosInstance = axios.create({
  baseURL: "https://api.atithiairportseva.com",
  headers: { "Content-Type": "application/json" },
});

const TOKEN_REFRESH_INTERVAL = 3 * 60 * 1000;
let tokenRefreshTimer = null;

const decryptToken = (encryptedToken) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedToken, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    return null;
  }
};

const encryptToken = (token) => {
  return CryptoJS.AES.encrypt(token, SECRET_KEY).toString();
};

const redirectToLogin = () => {
  window.location.href = "/";
};

const clearAuthTokens = (shouldRedirect = true) => {
  // Remove from sessionStorage instead of localStorage
  sessionStorage.removeItem("accessToken");
  sessionStorage.removeItem("refreshToken");
  sessionStorage.removeItem("instantToken");

  // Keep user in localStorage for persistence
  localStorage.removeItem("user");

  delete axiosInstance.defaults.headers.common["Authorization"];
  stopTokenRefreshTimer();
  store.dispatch(logoutRequest());

  if (shouldRedirect) {
    redirectToLogin();
  }
};

const handleNewBookingToken = () => {
  const encryptedNewBookingAccessToken = sessionStorage.getItem(
    "newBookingAccessToken",
  );

  if (encryptedNewBookingAccessToken) {
    const newBookingPages = [
      "/new-booking",
      "/new-booking/booking-passenger-details",
      "/new-booking/booking-summary",
      "/new-booking/airport-details",
    ];

    const isOnNewBookingPage = newBookingPages.includes(
      window.location.pathname,
    );

    if (isOnNewBookingPage) {
      const newBookingAccessToken = decryptToken(
        encryptedNewBookingAccessToken,
      );
      if (newBookingAccessToken) {
        return `Bearer ${newBookingAccessToken}`;
      }
    } else {
      sessionStorage.removeItem("newBookingAccessToken");
    }
  }
  return null;
};

// New function to handle new booking token setup on page load
const initializeNewBookingToken = () => {
  const encryptedNewBookingAccessToken = sessionStorage.getItem(
    "newBookingAccessToken",
  );

  if (encryptedNewBookingAccessToken) {
    const newBookingPages = [
      "/new-booking",
      "/new-booking/booking-passenger-details",
      "/new-booking/booking-summary",
      "/new-booking/airport-details",
    ];

    const isOnNewBookingPage = newBookingPages.includes(
      window.location.pathname,
    );

    if (isOnNewBookingPage) {
    } else {
      sessionStorage.removeItem("newBookingAccessToken");
    }
  }
};

window.addEventListener("storage", (event) => {
  // Handle cross-tab logout
  if (event.key === "logoutEvent") {
    clearAuthTokens(true);
    return;
  }

  // Handle new booking token
  if (event.key === "newBookingAccessToken" && event.newValue) {
    if (NEW_BOOKING_PAGES.includes(window.location.pathname)) {
      sessionStorage.setItem("newBookingAccessToken", event.newValue);
    }
  }
});
let tokenRefreshPromise = null;

async function handleTokenRefresh() {
  if (!tokenRefreshPromise) {
    const encryptedRefreshToken = sessionStorage.getItem("refreshToken");
    if (!encryptedRefreshToken) {
      clearAuthTokens();
      return Promise.reject("No refresh token found");
    }

    const decryptedRefreshToken = decryptToken(encryptedRefreshToken);
    if (!decryptedRefreshToken) {
      clearAuthTokens();
      return Promise.reject("Failed to decrypt refresh token");
    }

    tokenRefreshPromise = axiosInstance
      .post("/auth/refreshtoken", {
        token: decryptedRefreshToken,
      })
      .then((response) => {
        const { accessToken, refreshToken } = response.data;

        sessionStorage.setItem("accessToken", encryptToken(accessToken));
        sessionStorage.setItem("refreshToken", encryptToken(refreshToken));

        return accessToken;
      })
      .catch((error) => {
        clearAuthTokens(); // This will redirect to login
        throw error;
      })
      .finally(() => {
        tokenRefreshPromise = null;
      });
  }
  return tokenRefreshPromise;
}

function startTokenRefreshTimer() {
  if (tokenRefreshTimer) {
    clearInterval(tokenRefreshTimer);
  }

  tokenRefreshTimer = setInterval(async () => {
    try {
      await handleTokenRefresh();
    } catch (error) {
      stopTokenRefreshTimer();
      // Token refresh failed, user will be redirected to login by handleTokenRefresh
    }
  }, TOKEN_REFRESH_INTERVAL);
}

function stopTokenRefreshTimer() {
  if (tokenRefreshTimer) {
    clearInterval(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
}

axiosInstance.interceptors.request.use(
  (config) => {
    const newBookingAuth = handleNewBookingToken();
    if (newBookingAuth) {
      config.headers.Authorization = newBookingAuth;
      return config;
    }
    const encryptedAccessToken = sessionStorage.getItem("accessToken");
    const encryptedInstantToken = sessionStorage.getItem("instantToken");
    const user = localStorage.getItem("user");

    if (encryptedAccessToken || encryptedInstantToken) {
      const accessToken = decryptToken(
        encryptedAccessToken || encryptedInstantToken,
      );
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => {
    if (!tokenRefreshTimer && sessionStorage.getItem("refreshToken")) {
      startTokenRefreshTimer();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response) {
      const { status } = error.response;

      // Handle 401 (Unauthorized) - Token is invalid/expired
      if (status === 401) {
        clearAuthTokens(); // This will redirect to login
        localStorage.removeItem("newBookingAccessToken");
        return Promise.reject(error);
      }

      // Handle 403 (Forbidden) - Try to refresh token
      if (status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const newAccessToken = await handleTokenRefresh();
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          clearAuthTokens(); // This will redirect to login
          localStorage.removeItem("newBookingAccessToken");
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  },
);

const initializeAuthRefresh = () => {
  // First initialize new booking token logic
  initializeNewBookingToken();

  // Then handle regular token refresh
  handleNewBookingToken();

  const refreshToken = sessionStorage.getItem("refreshToken");
  if (refreshToken) {
    startTokenRefreshTimer();
  }
};

initializeAuthRefresh();

export {
  startTokenRefreshTimer,
  stopTokenRefreshTimer,
  clearAuthTokens,
  redirectToLogin,
};
export default axiosInstance;
